import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ORGANIZATION_SETTINGS_QUERY_KEY } from "@/hooks/queries";
import { useOrganizationSettingsQuery } from "@/hooks/queries/useOrganizationSettingsQuery";
import AutomatedEmailsCard from "@/components/settings/AutomatedEmailsCard";
import BrandingSection from "@/components/settings/BrandingSection";
import OrganizationSection from "@/components/settings/OrganizationSection";
import UnderwritingRulesCard from "@/components/settings/UnderwritingRulesCard";
import AiAssistantSection from "@/components/settings/AiAssistantSection";
import AiAccessSection from "@/components/settings/AiAccessSection";
import { canManageAgentAccess, canUseAgent } from "@/auth/capabilities";
import TrialBadge from "@/components/trial/TrialBadge";
import { useTrialAccount } from "@/hooks/useTrialAccount";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  verticalTabsListClass,
  verticalTabTriggerClass,
} from "@/components/ui/tabs";
import type { OrganizationSettings } from "@/services/organizationSettings";
import {
  resolveSettingsSection,
  sectionForOAuthReturn,
  takeContractOAuthFlash,
  visibleSettingsSections,
  canAccessSettings,
  type ContractOAuthFlash,
  type SettingsSectionId,
} from "./settingsSections";

interface SectionProps {
  settings: OrganizationSettings | undefined;
  loading: boolean;
}

/** One component per section id, so adding a section is a registry entry plus a component. */
const SECTION_COMPONENTS: Record<SettingsSectionId, React.FC<SectionProps>> = {
  branding: BrandingSection,
  organization: OrganizationSection,
  "underwriting-rules": UnderwritingRulesCard,
  emails: AutomatedEmailsCard,
  "ai-assistant": AiAssistantSection,
  "ai-access": AiAccessSection,
};

const Settings: React.FC = () => {
  const { isOrgManager, user } = useCurrentUser();
  const { isTrial, lockMessage } = useTrialAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { section: requestedSection } = useParams<{ section?: string }>();
  const [oauthFlash, setOauthFlash] = useState<ContractOAuthFlash | null>(null);
  const oauthFlashPainted = useRef(false);

  const commitOAuthFlash = useCallback((flash: ContractOAuthFlash | null) => {
    setOauthFlash(flash);
    if (!flash) oauthFlashPainted.current = false;
  }, []);

  const orgQuery = useOrganizationSettingsQuery(isOrgManager);
  const sections = visibleSettingsSections({
    isOrgManager,
    hasAgent: canUseAgent(user),
    managesAgentAccess: canManageAgentAccess(user),
  });

  // The OAuth callback can only return to `/setting`, so send it on to the
  // section that shows its result, keeping the query string it came back with.
  const oauthSection = sectionForOAuthReturn(location.search);
  const active = resolveSettingsSection(oauthSection ?? requestedSection, sections);

  // Keep the URL on a real section so a refresh, a bookmark, or the browser's
  // back button all land on the same tab the user is looking at. Same Route
  // (`/setting/:section?`) so this replace does not remount Settings.
  useEffect(() => {
    if (!active || active.id === requestedSection) return;
    navigate(`/setting/${active.id}${location.search}`, { replace: true });
  }, [active, requestedSection, navigate, location.search]);

  // Apply OAuth query params into Settings state after the section is in the
  // path. Do not clear on unmount (StrictMode's extra unmount would wipe the toast).
  useEffect(() => {
    if (!active || requestedSection !== active.id) return;
    const { flash, nextSearch } = takeContractOAuthFlash(location.search);
    if (!flash) return;
    commitOAuthFlash(flash);
    oauthFlashPainted.current = false;
    void queryClient.invalidateQueries({ queryKey: ORGANIZATION_SETTINGS_QUERY_KEY });
    navigate(`/setting/${active.id}${nextSearch}`, { replace: true });
  }, [active, requestedSection, location.search, navigate, commitOAuthFlash, queryClient]);

  useEffect(() => {
    if (oauthFlash) oauthFlashPainted.current = true;
  }, [oauthFlash]);

  const openSection = (id: SettingsSectionId) => {
    const { flash, nextSearch } = takeContractOAuthFlash(location.search);
    // First arrival: OAuth params still in the URL and we have not shown a flash yet.
    if (flash && oauthFlash == null) {
      commitOAuthFlash(flash);
      oauthFlashPainted.current = false;
      void queryClient.invalidateQueries({ queryKey: ORGANIZATION_SETTINGS_QUERY_KEY });
      navigate(`/setting/${id}${nextSearch}`);
      return;
    }
    // Later tab changes clear a flash that is already on screen (including the
    // race where OAuth params linger in the URL until the replace navigate lands).
    if (oauthFlashPainted.current || oauthFlash != null) commitOAuthFlash(null);
    navigate(`/setting/${id}${flash ? nextSearch : location.search}`);
  };

  const settings = orgQuery.data;
  const loading = orgQuery.isLoading;

  if (!canAccessSettings({ isOrgManager })) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
          {isTrial && <TrialBadge tooltip={lockMessage("generic")} />}
        </div>
        {isTrial && isOrgManager && (
          <p className="mt-1 text-sm text-muted-foreground">
            This is a trial account: greyed-out controls are locked to platform defaults. Hover any
            of them to see why.
          </p>
        )}
      </div>

      {oauthFlash && (
        <Alert
          variant={oauthFlash.type === "error" ? "destructive" : undefined}
          className={
            oauthFlash.type === "success"
              ? "border-green-200 bg-green-50 text-green-950 dark:border-green-900 dark:bg-green-950/30 dark:text-green-100"
              : undefined
          }
          data-testid="contract-oauth-flash"
        >
          <AlertDescription className="flex items-start justify-between gap-3">
            <span>{oauthFlash.message}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              aria-label="Dismiss"
              onClick={() => commitOAuthFlash(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {active && (
        <Tabs
          orientation="vertical"
          value={active.id}
          onValueChange={(value) => openSection(value as SettingsSectionId)}
          className="flex flex-col gap-4 md:flex-row md:items-start"
        >
          <Card className="w-full shrink-0 p-2 md:w-56 md:self-start">
            <TabsList className={verticalTabsListClass} aria-label="Settings sections">
              {sections.map((item) => (
                <TabsTrigger
                  key={item.id}
                  value={item.id}
                  className={verticalTabTriggerClass}
                  data-testid={`settings-tab-${item.id}`}
                >
                  {item.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Card>

          <div className="min-w-0 flex-1">
            {sections.map((item) => {
              const Section = SECTION_COMPONENTS[item.id];
              return (
                <TabsContent
                  key={item.id}
                  value={item.id}
                  forceMount
                  className="mt-0 data-[state=inactive]:hidden"
                >
                  <Section settings={settings} loading={loading} />
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      )}
    </div>
  );
};

export default Settings;
